/**
 * GrandRoundsView Page
 *
 * Displays the full Grand Rounds debate session with transcript,
 * consensus results, and real-time updates.
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Code,
  LayoutList,
  Stethoscope,
  Search,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import type {
  GrandRoundsStatus,
  ConsensusOutcome,
  GrandRoundsPhase,
  LLMProvider,
} from '../../lib/api';
import { DebateTranscript, ExpertBadge, ModeratorBadge, GrandRoundsFlowDiagram, ConsensusAnalysisDisplay } from './components';

// Status configuration
const STATUS_CONFIG: Record<GrandRoundsStatus, { label: string; icon: React.ReactNode; color: string }> = {
  PENDING: { label: 'Pending', icon: <Clock className="h-4 w-4" />, color: 'text-gray-500 bg-gray-100' },
  RUNNING: { label: 'In Progress', icon: <RefreshCw className="h-4 w-4 animate-spin" />, color: 'text-amber-600 bg-amber-100' },
  COMPLETED: { label: 'Completed', icon: <CheckCircle2 className="h-4 w-4" />, color: 'text-green-600 bg-green-100' },
  FAILED: { label: 'Failed', icon: <XCircle className="h-4 w-4" />, color: 'text-red-600 bg-red-100' },
  CANCELLED: { label: 'Cancelled', icon: <XCircle className="h-4 w-4" />, color: 'text-gray-500 bg-gray-100' },
};

const OUTCOME_CONFIG: Record<ConsensusOutcome, { label: string; description: string; color: string }> = {
  FULL_CONSENSUS: {
    label: 'Full Consensus',
    description: 'All experts agree on recommendations',
    color: 'text-green-700 bg-green-50 border-green-200',
  },
  MAJORITY_CONSENSUS: {
    label: 'Majority Consensus',
    description: 'Most experts agree with minor differences',
    color: 'text-blue-700 bg-blue-50 border-blue-200',
  },
  PARTIAL_CONSENSUS: {
    label: 'Partial Consensus',
    description: 'Agreement on some points, divergence on others',
    color: 'text-amber-700 bg-amber-50 border-amber-200',
  },
  STABLE_DIVERGENCE: {
    label: 'Stable Divergence',
    description: 'Experts maintain different positions after full debate',
    color: 'text-purple-700 bg-purple-50 border-purple-200',
  },
};

// User-friendly phase configuration with icons and descriptions
const PHASE_CONFIG: Record<GrandRoundsPhase, {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  bgColor: string;
  borderColor: string;
  iconBgColor: string;
}> = {
  PHASE_1_CRITERIA: {
    title: 'Phase 2+3: Case Analysis',
    subtitle: 'Analyzing patient case and generating matching criteria',
    description: 'Our expert panel is evaluating the medical indication, patient expectations, and defining the optimal matching criteria for finding the right care provider.',
    icon: <Stethoscope className="h-5 w-5" />,
    bgColor: 'bg-gradient-to-r from-blue-50 to-indigo-50',
    borderColor: 'border-blue-200',
    iconBgColor: 'bg-blue-100 text-blue-700',
  },
  PHASE_2_REFERRALS: {
    title: 'Phase 4: Care Provider Matching',
    subtitle: 'Matching the best care providers',
    description: 'Our expert panel is matching and evaluating care providers based on the defined matching criteria, ranking them by suitability for your patient.',
    icon: <Search className="h-5 w-5" />,
    bgColor: 'bg-gradient-to-r from-emerald-50 to-teal-50',
    borderColor: 'border-emerald-200',
    iconBgColor: 'bg-emerald-100 text-emerald-700',
  },
};

export function GrandRoundsView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [viewMode, setViewMode] = useState<'normal' | 'expert'>('normal');

  // Fetch session data
  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['grand-rounds', id],
    queryFn: () => api.grandRounds.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      // Poll while session is active
      if (data && ['PENDING', 'RUNNING'].includes(data.status)) {
        return 3000;
      }
      return false;
    },
  });

  const session = response?.data;

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => api.grandRounds.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grand-rounds', id] });
    },
  });

  // SSE subscription for real-time updates
  useEffect(() => {
    if (!id || !session || ['COMPLETED', 'FAILED', 'CANCELLED'].includes(session.status)) {
      return;
    }

    let cleanupFn: (() => void) | null = null;

    // subscribeToEvents is async (needs to get auth token)
    api.grandRounds.subscribeToEvents(id, (event) => {
      // Refetch on significant events
      if (['round_completed', 'session_completed', 'consensus_reached', 'session_failed'].includes(event.type)) {
        refetch();
      }
    }).then((cleanup) => {
      cleanupFn = cleanup;
      setIsSubscribed(true);
    }).catch((err) => {
      console.error('[GrandRoundsView] Failed to subscribe to events:', err);
    });

    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
      setIsSubscribed(false);
    };
  }, [id, session?.status, refetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-gray-700 font-medium">Failed to load session</p>
          <p className="text-sm text-gray-500 mt-1">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[session.status];
  const outcomeConfig = session.outcomeType ? OUTCOME_CONFIG[session.outcomeType] : null;
  const isActive = ['PENDING', 'RUNNING'].includes(session.status);

  // Calculate completed rounds
  const completedRounds = session.rounds?.filter((r) => r.status === 'COMPLETED').length || 0;

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">Grand Rounds Session</h1>
              <span
                className={clsx(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium',
                  statusConfig.color
                )}
              >
                {statusConfig.icon}
                {statusConfig.label}
              </span>
              {isSubscribed && isActive && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Live
                </span>
              )}
              {/* View Mode Toggle */}
              <div className="ml-4 flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('normal')}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium transition-colors',
                    viewMode === 'normal'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  <LayoutList className="h-4 w-4" />
                  Normal
                </button>
                <button
                  onClick={() => setViewMode('expert')}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium transition-colors',
                    viewMode === 'expert'
                      ? 'bg-purple-100 text-purple-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  <Code className="h-4 w-4" />
                  Expert
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              <Link
                to={`/validation/test-runs/${session.testRunId}/results`}
                className="text-blue-600 hover:text-blue-800"
              >
                View Test Run
                <ExternalLink className="inline h-3 w-3 ml-1" />
              </Link>
            </p>
          </div>
        </div>

        {isActive && (
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Session'}
          </button>
        )}
      </div>

      {/* Phase Banner - Prominent user-friendly phase indicator */}
      {(() => {
        const phaseConfig = PHASE_CONFIG[session.phase];
        return (
          <div className={clsx('rounded-xl border-2 p-5', phaseConfig.bgColor, phaseConfig.borderColor)}>
            <div className="flex items-start gap-4">
              <div className={clsx('rounded-lg p-2.5 shrink-0', phaseConfig.iconBgColor)}>
                {phaseConfig.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold text-gray-900">{phaseConfig.title}</h2>
                  {isActive && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      In Progress
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{phaseConfig.subtitle}</p>
                <p className="text-sm text-gray-500 mt-2">{phaseConfig.description}</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Outcome Banner (when completed) */}
      {session.status === 'COMPLETED' && outcomeConfig && (
        <div className={clsx('rounded-lg border p-4', outcomeConfig.color)}>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold">{outcomeConfig.label}</h3>
              <p className="text-sm mt-0.5">{outcomeConfig.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner (when failed) */}
      {session.status === 'FAILED' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">Session Failed</h3>
              <p className="text-sm text-red-700 mt-0.5">
                The debate session encountered an error and could not complete.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* View Mode Content */}
      {viewMode === 'expert' ? (
        /* Expert View - Flow Diagram */
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <GrandRoundsFlowDiagram session={session} />
        </div>
      ) : (
        /* Normal View */
        <>
          {/* Expert Panel */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 mb-3">Expert Panel</h2>
            <div className="flex flex-wrap gap-3">
              <ExpertBadge model="CLAUDE_OPUS_4_6" size="lg" />
              <ExpertBadge model="GPT_5_2_PRO" size="lg" />
              <ExpertBadge model="GEMINI_3_PRO" size="lg" />
              <div className="border-l border-gray-200 mx-2" />
              <ModeratorBadge size="lg" />
            </div>
          </div>

          {/* Consensus Result (when consensus achieved) */}
          {session.consensusResult && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 mb-3">Consensus Recommendation</h2>
              {typeof session.consensusResult === 'object' ? (
                <ConsensusAnalysisDisplay
                  result={session.consensusResult as Record<string, unknown>}
                  outcomeType={session.outcomeType}
                />
              ) : (
                <p className="text-gray-600">{String(session.consensusResult)}</p>
              )}
            </div>
          )}

          {/* Moderator Summary */}
          {session.moderatorSummary && (
            <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-4">
              <h2 className="font-semibold text-indigo-900 mb-2">Moderator Summary</h2>
              <p className="text-sm text-indigo-800 whitespace-pre-wrap">{session.moderatorSummary}</p>
            </div>
          )}

          {/* Divergent Options (when no full consensus) */}
          {session.divergentOptions && session.divergentOptions.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 mb-3">Divergent Positions</h2>
              <p className="text-sm text-gray-500 mb-4">
                The experts could not reach full consensus on these points:
              </p>
              <div className="space-y-4">
                {session.divergentOptions.map((option, i) => (
                  <DivergentOptionCard key={i} option={option as unknown as DivergentOption} />
                ))}
              </div>
            </div>
          )}

          {/* Debate Transcript */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 mb-4">Debate Transcript</h2>
            {session.rounds && session.rounds.length > 0 ? (
              <DebateTranscript rounds={session.rounds} />
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                {isActive ? 'Debate is starting...' : 'No debate rounds recorded'}
              </p>
            )}
          </div>
        </>
      )}

      {/* Session Metadata */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-700 mb-3 text-sm">Session Details</h2>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">Session ID</dt>
            <dd className="font-mono text-gray-900 truncate">{session.id}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Rounds Completed</dt>
            <dd className="text-gray-900">{completedRounds} / {session.totalRounds}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Started</dt>
            <dd className="text-gray-900">
              {session.startedAt
                ? new Date(session.startedAt).toLocaleString()
                : 'Not started'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Completed</dt>
            <dd className="text-gray-900">
              {session.completedAt
                ? new Date(session.completedAt).toLocaleString()
                : '-'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// Helper components

interface DivergentOption {
  point: string;
  positions: Array<{
    model: string;
    position: string;
    reasoning: string;
  }>;
  clinicianGuidance?: string;
}

function DivergentOptionCard({ option }: { option: DivergentOption }) {
  return (
    <div className="border border-amber-200 rounded-lg overflow-hidden">
      <div className="bg-amber-50 px-4 py-2 border-b border-amber-200">
        <h4 className="font-medium text-amber-800">{option.point}</h4>
      </div>
      <div className="p-4 space-y-3">
        {option.positions?.map((pos, i) => (
          <div key={i} className="flex items-start gap-3">
            <ExpertBadge model={pos.model as LLMProvider} size="sm" />
            <div>
              <p className="text-sm font-medium text-gray-700">{pos.position}</p>
              <p className="text-xs text-gray-500 mt-0.5">{pos.reasoning}</p>
            </div>
          </div>
        ))}
        {option.clinicianGuidance && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-blue-700">
              <strong>Guidance:</strong> {option.clinicianGuidance}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
